import { useEffect, useState } from 'react';
import { dec, formatMoney } from '@makila/fiscal';
import { Cake, Contact, MessageCircle, Plus } from 'lucide-react';
import clsx from 'clsx';
import { api, patch, post } from './api';
import { useBusiness } from './business';
import { ManagedByMakila, useMakilaLinked } from './makila';
import { mzDate } from '../lib/receipt';
import { Alert, Badge, Button, Card, EmptyState, Field, FormActions, Input, NuitInput, NumberInput, PhoneInput, Section, Segmented, Select, Skeleton, Textarea, Toggle, useToast } from './ui';
import { attempt, useLoad } from './useLoad';

interface Customer {
  id: string; name: string; nuit: string | null; phone: string | null; email: string | null; address: string; active: boolean; created_by_terminal: string | null;
  whatsapp: string | null; birthday: string | null; gender: 'f' | 'm' | 'x' | null; notes: string | null; tags: string[]; marketing_consent: boolean; loyalty_points: number;
  credit_limit: string | null; credit_balance: string; credit_terms_days: number; member_until?: string | null;
}
interface Detail {
  customer: Customer & { consent_at: string | null };
  history: { id: string; doc_type: string; full_number: string; issue_date: string; gross_total: string; training: boolean }[];
  stats: { documents: number; total: string; lastVisit: string | null; average: string };
  openInvoices: { id: string; full_number: string; issue_date: string; due_date: string; gross_total: string; open: string }[];
  packages: { id: string; name: string; sessions_total: number; sessions_used: number; expires_at: string | null; created_at: string }[];
}

const blank = { name: '', nuit: '', phone: '', email: '', address: '', whatsapp: '', birthday: '', gender: '', notes: '', tags: '', marketingConsent: false, active: true, creditLimit: '', creditTermsDays: '30' };
type Draft = typeof blank;
const draftOf = (c: Customer): Draft => ({
  name: c.name, nuit: c.nuit ?? '', phone: c.phone ?? '', email: c.email ?? '', address: c.address ?? '', whatsapp: c.whatsapp ?? '', birthday: c.birthday ?? '',
  gender: c.gender ?? '', notes: c.notes ?? '', tags: c.tags.join(', '), marketingConsent: c.marketing_consent, active: c.active,
  creditLimit: c.credit_limit ? String(Number(c.credit_limit)).replace('.', ',') : '', creditTermsDays: String(c.credit_terms_days ?? 30),
});
/** Ligação de WhatsApp com o número em formato internacional (sem custo: abre a conversa). */
export const waLink = (n: string, text = '') => `https://wa.me/${n.replace(/\D/g, '').replace(/^(8\d{8})$/, '258$1')}${text ? `?text=${encodeURIComponent(text)}` : ''}`;
const birthdaySoon = (b: string | null) => {
  if (!b) return false;
  const now = new Date();
  const next = new Date(now.getFullYear(), Number(b.slice(5, 7)) - 1, Number(b.slice(8, 10)));
  const days = (next.getTime() - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / 86_400_000;
  return days >= 0 && days <= 7;
};

/** Clientes (docs/14 §5): chegam às caixas pelo catálogo; os criados no caixa aparecem assinalados. */
export function CustomersPage() {
  const [q, setQ] = useState('');
  const { data, error, reload } = useLoad<{ items: Customer[] }>(`/customers${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);
  const [open, setOpen] = useState<string | 'new' | null>(null);
  const linked = useMakilaLinked();
  const { type } = useBusiness();

  if (open) return <CustomerSheet id={open === 'new' ? null : open} linked={linked} onClose={(changed) => { setOpen(null); if (changed) void reload(); }} />;

  return (
    <div className="max-w-5xl space-y-6">
      {linked && <ManagedByMakila what="clientes (os dados de contacto e o NUIT)" />}
      <Card title={type.terms.customers} padded={false} actions={
        <div className="flex flex-wrap gap-2">
          <Input aria-label="Pesquisar clientes" placeholder="Nome ou NUIT" value={q} onChange={(e) => setQ(e.target.value)} className="w-60" />
          {!linked && <Button icon={Plus} onClick={() => setOpen('new')}>Novo</Button>}
        </div>
      }>
        {error && <div className="p-4"><Alert>{error}</Alert></div>}
        {!data && !error && <div className="p-4"><Skeleton rows={5} /></div>}
        {data && !data.items.length && (
          <EmptyState icon={Contact} title={q ? 'Nenhum cliente encontrado' : `Ainda não há ${type.terms.customers.toLowerCase()}`}
            text="Os clientes também se criam na caixa, no momento da venda." action={!linked && !q && <Button icon={Plus} onClick={() => setOpen('new')}>Novo</Button>} />
        )}
        <ul className="divide-y divide-[var(--border)] p-2 xl:p-3">
          {data?.items.map((c) => (
            <li key={c.id}>
              <button onClick={() => setOpen(c.id)} className={clsx('flex w-full flex-wrap items-center gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-surface-2', !c.active && 'opacity-50')}>
                <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-surface-2 font-semibold">{c.name.slice(0, 1).toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5 font-medium">
                    {c.name}
                    {c.created_by_terminal && <Badge>criado no caixa</Badge>}
                    {birthdaySoon(c.birthday) && <Badge tone="brand"><Cake size={12} className="mr-1" />aniversário</Badge>}
                    {c.tags.slice(0, 3).map((t) => <Badge key={t} tone="info">{t}</Badge>)}
                  </span>
                  <span className="block text-xs text-muted">{[c.nuit ? `NUIT ${c.nuit}` : 'sem NUIT', c.phone, c.loyalty_points > 0 && `${c.loyalty_points} pontos`].filter(Boolean).join(' · ')}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function CustomerSheet({ id, linked, onClose }: { id: string | null; linked: boolean; onClose: (changed: boolean) => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [f, setF] = useState<Draft>(blank);
  const [tab, setTab] = useState<'ficha' | 'historico' | 'saldos' | 'saude'>('ficha');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { has } = useBusiness();
  const toast = useToast();
  useEffect(() => {
    if (!id) return;
    void api<Detail>(`/customers/${id}`).then((x) => { setD(x); setF(draftOf(x.customer)); }, () => setErr('Sem ligação à internet.'));
  }, [id]);
  if (id && !d) return <Card>{err ? <Alert>{err}</Alert> : <Skeleton rows={6} />}</Card>;
  const up = <K extends keyof Draft>(k: K, v: Draft[K]) => setF({ ...f, [k]: v });

  const save = async () => {
    setBusy(true);
    let cid = id;
    const e = await attempt(async () => {
      if (!linked) {
        const base = { name: f.name.trim(), nuit: f.nuit || null, phone: f.phone || null, email: f.email.trim(), address: f.address.trim(), active: f.active };
        if (cid) await patch(`/customers/${cid}`, base);
        else cid = (await post<{ customer: { id: string } }>('/customers', base)).customer.id;
      }
      await patch(`/customers/${cid}/profile`, {
        whatsapp: f.whatsapp || null, birthday: f.birthday || null, gender: f.gender || null, notes: f.notes,
        tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean), marketingConsent: f.marketingConsent,
        ...(has('credit') && !linked ? { creditLimit: f.creditLimit.trim() ? f.creditLimit.trim() : null, creditTermsDays: Number(f.creditTermsDays) || 0 } : {}),
      });
    });
    setBusy(false);
    setErr(e);
    if (!e) { toast(id ? 'Cliente gravado' : 'Cliente criado'); onClose(true); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <Card title={id ? d!.customer.name : 'Novo cliente'} description={id && d ? `${d.stats.documents} documentos · ${formatMoney(d.stats.total)} MT${d.stats.lastVisit ? ` · última visita ${mzDate(d.stats.lastVisit)}` : ''}` : undefined}
        actions={id && f.whatsapp ? <a href={waLink(f.whatsapp)} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-lg border border-base px-4 font-semibold hover:bg-surface-2"><MessageCircle size={18} className="text-green-600" /> WhatsApp</a> : undefined}>
        {id && (
          <div className="mb-6">
            <Segmented label="Secções do cliente" value={tab} onChange={setTab} options={[{ value: 'ficha', label: 'Ficha' }, { value: 'historico', label: 'Histórico' }, { value: 'saldos', label: 'Saldos' }, ...(has('health') ? [{ value: 'saude' as const, label: 'Saúde' }] : [])]} />
          </div>
        )}

        {tab === 'ficha' && (
          <div className="space-y-8">
            {linked && <Alert tone="info">O nome, o NUIT e os contactos vêm do Makila Facturação. Aqui completa a ficha do POS.</Alert>}
            <Section title="Identificação">
              <Field label="Nome" required><Input required disabled={linked} value={f.name} onChange={(e) => up('name', e.target.value)} /></Field>
              <Field label="NUIT" hint="Obrigatório para facturar a empresas"><NuitInput disabled={linked} value={f.nuit} onChange={(v) => up('nuit', v)} /></Field>
              <Field label="Data de nascimento" hint="Para lembrar o aniversário"><Input type="date" className="w-45" value={f.birthday} onChange={(e) => up('birthday', e.target.value)} /></Field>
              <Field label="Género" hint="Opcional">
                <Select className="w-45" value={f.gender} onChange={(e) => up('gender', e.target.value)}>
                  <option value="">—</option><option value="f">Feminino</option><option value="m">Masculino</option><option value="x">Outro / prefere não dizer</option>
                </Select>
              </Field>
            </Section>
            <Section title="Contactos">
              <Field label="Telefone"><PhoneInput disabled={linked} value={f.phone} onChange={(v) => up('phone', v)} /></Field>
              <Field label="WhatsApp" hint="Para lembretes e avisos"><PhoneInput value={f.whatsapp} onChange={(v) => up('whatsapp', v)} /></Field>
              <Field label="Email"><Input type="email" disabled={linked} value={f.email} onChange={(e) => up('email', e.target.value)} /></Field>
              <Field label="Morada"><Input disabled={linked} value={f.address} onChange={(e) => up('address', e.target.value)} /></Field>
            </Section>
            <Section title="Notas e preferências">
              <Field label="Etiquetas" hint="Separadas por vírgulas (ex.: vip, empresa, Matola)"><Input value={f.tags} onChange={(e) => up('tags', e.target.value)} /></Field>
              <Toggle checked={f.marketingConsent} onChange={(v) => up('marketingConsent', v)} label="Aceita receber promoções"
                description={d?.customer.consent_at && f.marketingConsent ? `Consentimento dado a ${mzDate(d.customer.consent_at.slice(0, 10))}` : 'Só se o cliente disser que sim (fica registada a data)'} />
              <div className="sm:col-span-2"><Field label="Notas" hint="Preferências, cuidados, o que a equipa deve saber"><Textarea maxLength={2000} rows={3} value={f.notes} onChange={(e) => up('notes', e.target.value)} /></Field></div>
            </Section>
            {has('credit') && !linked && (
              <Section title="Conta corrente" description="Venda a crédito (factura): só com NUIT e com limite. Acima do limite, a caixa pede um gerente.">
                <Field label="Limite de crédito" hint={f.nuit ? 'Vazio = sem crédito' : 'Precisa do NUIT do cliente'}><NumberInput suffix="MT" className="w-48" disabled={!f.nuit} value={f.creditLimit} onChange={(e) => up('creditLimit', e.target.value)} placeholder="sem crédito" /></Field>
                <Field label="Prazo de pagamento" hint="A data de vencimento de cada factura"><NumberInput suffix="dias" inputMode="numeric" className="w-36" value={f.creditTermsDays} onChange={(e) => up('creditTermsDays', e.target.value.replace(/\D/g, ''))} /></Field>
              </Section>
            )}
            {!linked && id && (
              <Section title="Estado">
                <Toggle checked={f.active} onChange={(v) => up('active', v)} label="Cliente activo" description="Um cliente inactivo deixa de aparecer na caixa" />
              </Section>
            )}
          </div>
        )}

        {tab === 'historico' && d && (
          d.history.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted"><tr><th className="py-2">Data</th><th>Documento</th><th className="text-right">Valor</th></tr></thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {d.history.map((h) => (
                    <tr key={h.id}>
                      <td className="py-2.5">{mzDate(h.issue_date)}</td>
                      <td className="font-mono">{h.full_number}{h.training && <Badge>formação</Badge>}</td>
                      <td className="num text-right">{h.doc_type === 'NC' ? '−' : ''}{formatMoney(h.gross_total)} MT</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-muted">Últimos 50 documentos. Talão médio: {formatMoney(d.stats.average)} MT.</p>
            </div>
          ) : <EmptyState icon={Contact} title="Ainda sem compras" text="As vendas com este cliente aparecem aqui." />
        )}

        {tab === 'saldos' && d && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Pontos" value={has('loyalty') ? String(d.customer.loyalty_points) : '—'} hint={has('loyalty') ? '1 ponto = 1 MT de desconto' : 'Fidelização desligada'} />
            <Stat label="Total comprado" value={`${formatMoney(d.stats.total)} MT`} hint={`${d.stats.documents} documentos`} />
            <Stat label="Talão médio" value={`${formatMoney(d.stats.average)} MT`} />
            {has('memberships') && <Stat label="Mensalidade" value={d.customer.member_until ? `até ${mzDate(d.customer.member_until)}` : '—'} hint={d.customer.member_until && d.customer.member_until < new Date().toISOString().slice(0, 10) ? 'Em atraso' : 'Em dia'} />}
            {has('packages') && <Stat label="Sessões por usar" value={String(d.packages.reduce((n, k) => n + Math.max(0, k.sessions_total - k.sessions_used), 0))} hint={`${d.packages.length} pacotes`} />}
            {has('packages') && d.packages.length > 0 && (
              <div className="sm:col-span-3">
                <h3 className="mb-2 mt-3 font-display font-semibold">Pacotes</h3>
                <ul className="divide-y divide-[var(--border)] rounded-xl border border-base">
                  {d.packages.map((k) => (
                    <li key={k.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm">
                      <span className="flex-1 font-medium">{k.name}</span>
                      <span className="num">{k.sessions_used} de {k.sessions_total} usadas</span>
                      <span className="text-muted">{k.expires_at ? `válido até ${mzDate(k.expires_at)}` : 'sem validade'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {has('credit') && <Stat label="Conta corrente" value={`${formatMoney(d.customer.credit_balance)} MT`} hint={d.customer.credit_limit ? `Em dívida · limite ${formatMoney(d.customer.credit_limit)} MT` : 'Em dívida · sem crédito aprovado'} />}
            {has('credit') && d.openInvoices.length > 0 && (
              <div className="sm:col-span-3">
                <h3 className="mb-2 mt-3 font-display font-semibold">Facturas por pagar</h3>
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted"><tr><th className="py-2">Factura</th><th>Emitida</th><th>Vence</th><th className="text-right">Em falta</th></tr></thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {d.openInvoices.map((x) => {
                      const late = x.due_date < new Date().toISOString().slice(0, 10);
                      return (
                        <tr key={x.id}>
                          <td className="py-2.5 font-mono">{x.full_number}</td><td>{mzDate(x.issue_date)}</td>
                          <td className={late ? 'font-semibold text-brand-700' : ''}>{mzDate(x.due_date)}{late && ' · vencida'}</td>
                          <td className="num text-right">{formatMoney(x.open)} MT</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="mt-2 text-xs text-muted">O cliente paga na caixa (Receber), com recibo; abatem-se primeiro as mais antigas.</p>
              </div>
            )}
          </div>
        )}

        {tab === 'saude' && id && <HealthTab customerId={id} />}

        {err && <div className="mt-6"><Alert>{err}</Alert></div>}
        <FormActions>
          {tab === 'ficha' && <Button onClick={save} disabled={busy}>{busy ? 'A gravar…' : 'Gravar'}</Button>}
          <Button variant="ghost" onClick={() => onClose(false)}>{tab === 'ficha' ? 'Cancelar' : 'Voltar'}</Button>
        </FormActions>
      </Card>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl bg-surface-2 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className="num mt-1 text-2xl font-bold">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

interface Account { id: string; name: string; nuit: string | null; phone: string | null; credit_limit: string | null; credit_balance: string; current: string; d30: string; d60: string; d90: string; oldest_due: string | null }

/** Contas correntes (docs/14, Fase 0 passo 8): quem deve, quanto, e há quanto tempo está vencido. */
export function AccountsPage() {
  const { data, error } = useLoad<{ items: Account[] }>('/customers/accounts/open');
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Card><Skeleton rows={5} /></Card>;
  const sum = (k: keyof Account) => data.items.reduce((s, x) => s + Number(x[k] ?? 0), 0);
  const cols: [keyof Account, string][] = [['current', 'Por vencer'], ['d30', 'Vencido até 30 d'], ['d60', '31 a 60 d'], ['d90', 'Mais de 60 d']];
  return (
    <div className="max-w-6xl space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <Stat label="Total em dívida" value={`${formatMoney(sum('credit_balance').toFixed(2))} MT`} hint={`${data.items.length} clientes`} />
        {cols.map(([k, l]) => <Stat key={k} label={l} value={`${formatMoney(sum(k).toFixed(2))} MT`} />)}
      </div>
      <Card title="Clientes com facturas por pagar" padded={false}>
        {!data.items.length ? <EmptyState icon={Contact} title="Ninguém deve nada" text="As vendas a crédito aparecem aqui até serem pagas." /> : (
          <div className="overflow-x-auto p-2 xl:p-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted">
                <tr><th className="px-2 py-2">Cliente</th>{cols.map(([k, l]) => <th key={k} className="hidden px-2 text-right md:table-cell">{l}</th>)}<th className="px-2 text-right">Em dívida</th><th className="w-12" /></tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {data.items.map((a) => {
                  const over = a.credit_limit !== null && Number(a.credit_balance) > Number(a.credit_limit);
                  return (
                    <tr key={a.id}>
                      <td className="px-2 py-2.5"><div className="font-medium">{a.name}</div><div className="text-xs text-muted">{a.nuit ? `NUIT ${a.nuit}` : ''}{a.oldest_due ? ` · mais antiga vence ${mzDate(a.oldest_due)}` : ''}</div></td>
                      {cols.map(([k]) => <td key={k} className={clsx('num hidden px-2 text-right md:table-cell', k !== 'current' && Number(a[k]) > 0 && 'font-semibold text-brand-700')}>{Number(a[k]) ? formatMoney(String(a[k])) : '—'}</td>)}
                      <td className="num px-2 text-right font-bold">{formatMoney(a.credit_balance)}{over && <div><Badge tone="warn">acima do limite</Badge></div>}</td>
                      <td className="px-2 text-right">
                        {a.phone && <a aria-label={`Lembrar ${a.name} por WhatsApp`} title="Lembrete por WhatsApp" target="_blank" rel="noreferrer"
                          href={waLink(a.phone, `Bom dia. Lembramos que tem ${formatMoney(a.credit_balance)} MT em facturas por pagar. Obrigado.`)}
                          className="inline-grid h-10 w-10 place-items-center rounded-lg text-green-600 hover:bg-surface-2"><MessageCircle size={18} /></a>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

interface Health { allergies: string | null; conditions: string | null; medications: string | null; pregnant: boolean; notes: string | null; consent_at: string; updated_at: string }

/**
 * Ficha de saúde (decisão F7): só com o consentimento do cliente; vêem-na o
 * dono, o gerente e os profissionais; apaga-se a pedido. Cada abertura fica
 * registada na auditoria.
 */
function HealthTab({ customerId }: { customerId: string }) {
  const [h, setH] = useState<Health | null | undefined>(undefined);
  const [f, setF] = useState({ allergies: '', conditions: '', medications: '', pregnant: false, notes: '', consent: false });
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  const load = () => api<{ health: Health | null }>(`/customers/${customerId}/health`).then((r) => {
    setH(r.health);
    setF({ allergies: r.health?.allergies ?? '', conditions: r.health?.conditions ?? '', medications: r.health?.medications ?? '', pregnant: r.health?.pregnant ?? false, notes: r.health?.notes ?? '', consent: !!r.health });
  }, (e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (h === undefined) return err ? <Alert>{err}</Alert> : <Skeleton rows={4} />;
  return (
    <div className="space-y-6">
      <Alert tone="info">Dados de saúde: vêem-nos só o dono, os gerentes e os profissionais. Cada consulta fica registada. Apague-os se o cliente pedir.</Alert>
      <Toggle checked={f.consent} disabled={!!h} onChange={(v) => setF({ ...f, consent: v })} label="O cliente consentiu que se guardem estes dados"
        description={h ? `Consentimento registado a ${mzDate(h.consent_at.slice(0, 10))}` : 'Obrigatório antes de gravar (de preferência por escrito)'} />
      <Section title="Saúde e cuidados" cols={1}>
        <Field label="Alergias" hint="Produtos, óleos, látex, medicamentos…"><Textarea rows={2} value={f.allergies} maxLength={1000} onChange={(e) => setF({ ...f, allergies: e.target.value })} /></Field>
        <Field label="Contra-indicações e problemas de saúde" hint="Tensão alta, varizes, problemas de pele, cirurgias recentes…"><Textarea rows={2} value={f.conditions} maxLength={1000} onChange={(e) => setF({ ...f, conditions: e.target.value })} /></Field>
        <Field label="Medicação habitual"><Textarea rows={2} value={f.medications} maxLength={1000} onChange={(e) => setF({ ...f, medications: e.target.value })} /></Field>
        <Toggle checked={f.pregnant} onChange={(v) => setF({ ...f, pregnant: v })} label="Grávida" description="Muitos tratamentos e óleos não se fazem na gravidez" />
        <Field label="Notas"><Textarea rows={3} value={f.notes} maxLength={2000} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      </Section>
      {err && <Alert>{err}</Alert>}
      <div className="flex flex-wrap gap-2">
        <Button disabled={!f.consent} onClick={async () => {
          const e = await attempt(() => api(`/customers/${customerId}/health`, { method: 'PUT', body: JSON.stringify(f) }));
          setErr(e); if (!e) { toast('Ficha de saúde gravada'); void load(); }
        }}>Gravar ficha de saúde</Button>
        {h && <Button variant="danger" onClick={async () => {
          if (!confirm('Apagar a ficha de saúde deste cliente? Não se pode desfazer.')) return;
          const e = await attempt(() => api(`/customers/${customerId}/health`, { method: 'DELETE' }));
          setErr(e); if (!e) { toast('Ficha de saúde apagada'); void load(); }
        }}>Apagar a pedido do cliente</Button>}
      </div>
    </div>
  );
}

interface Card_ { id: string; code: string; amount: string; balance: string; expires_at: string | null; buyer_name: string | null; active: boolean; created_at: string }

/** Vales-oferta vendidos (decisão F2): valor, saldo, validade e quem comprou. */
export function GiftcardsPage() {
  const { data, error } = useLoad<{ items: Card_[] }>('/customers/giftcards/all');
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Card><Skeleton rows={5} /></Card>;
  const today = new Date().toISOString().slice(0, 10);
  const open = data.items.filter((g) => Number(g.balance) > 0 && (!g.expires_at || g.expires_at >= today));
  return (
    <div className="max-w-5xl space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Vales vendidos" value={String(data.items.length)} />
        <Stat label="Por usar (em validade)" value={`${formatMoney(open.reduce((s, g) => s.plus(g.balance), dec(0)).toFixed(2))} MT`} hint={`${open.length} vales`} />
        <Stat label="Já usado" value={`${formatMoney(data.items.reduce((s, g) => s.plus(g.amount).minus(g.balance), dec(0)).toFixed(2))} MT`} />
      </div>
      <Alert tone="info">Vendem-se na caixa (Vender vale-oferta), sem IVA; o IVA sai na factura da compra em que o vale é usado.</Alert>
      <Card title="Vales" padded={false}>
        {!data.items.length ? <EmptyState icon={Contact} title="Ainda não se vendeu nenhum vale" /> : (
          <div className="overflow-x-auto p-2 xl:p-3">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted"><tr><th className="px-2 py-2">Código</th><th className="px-2">Comprado por</th><th className="px-2">Validade</th><th className="px-2 text-right">Valor</th><th className="px-2 text-right">Saldo</th></tr></thead>
              <tbody className="divide-y divide-[var(--border)]">
                {data.items.map((g) => {
                  const expired = g.expires_at && g.expires_at < today;
                  return (
                    <tr key={g.id} className={expired || !Number(g.balance) ? 'opacity-60' : ''}>
                      <td className="px-2 py-2.5 font-mono font-semibold">{g.code}</td>
                      <td className="px-2">{g.buyer_name ?? '—'}<div className="text-xs text-muted">{mzDate(g.created_at.slice(0, 10))}</div></td>
                      <td className="px-2">{g.expires_at ? <>{mzDate(g.expires_at)}{expired && <Badge tone="warn">expirado</Badge>}</> : 'sem prazo'}</td>
                      <td className="num px-2 text-right">{formatMoney(g.amount)}</td>
                      <td className="num px-2 text-right font-bold">{formatMoney(g.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
